import React, { Fragment } from "react";
import {
  Control,
  useFieldArray,
  UseFormRegister,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";

import { DEFAULT_CREDIT_PROVIDER_DATA } from "./defaultData";
import CTAButton from "components/CTA/Button";
import FormInputCurrency from "components/Form/Input/Currency";
import FormInputSelect from "components/Form/Input/Select";
import FormInputText from "components/Form/Input/Text";
import { ComonObjectType } from "ts/types";
import { LABELS } from "./labels";
import { ExpenditureCreditProviderTypes } from "./options";
import FormInputToggle from "components/Form/Input/Toggle";

interface StepProps {
  control: Control<any, any>;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  idKey: string;
  errorKey: ComonObjectType | undefined;
}

const STEP_KEY = "creditProviders";
const STYLE_TABLE_HEADER =
  "border-b border-gray-300 bg-gray-50 py-3 px-1.5 text-left text-sm font-semibold text-gray-900";
const STYLE_TABLE_CELL = "whitespace-nowwrap px-1 text-sm text-gray-600";

const CreditProviders: React.FC<StepProps> = ({
  control,
  register,
  setValue,
  idKey,
  errorKey,
}) => {
  const { fields, remove, append } = useFieldArray({
    control,
    name: `${idKey}.${STEP_KEY}`,
  });

  const applicantsCredit = useWatch({
    name: `${idKey}.${STEP_KEY}`,
    control: control,
  });

  const gridStyle = "grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_1fr_40px]";

  return (
    <div className="flex flex-col space-y-4">
      <div className={`grid ${gridStyle} gap-y-2`}>
        <div className={STYLE_TABLE_HEADER}>
          {LABELS.creditProviders.providerType}
        </div>
        <div className={STYLE_TABLE_HEADER}>
          {LABELS.creditProviders.provider}
        </div>
        <div className={STYLE_TABLE_HEADER}>
          {LABELS.creditProviders.balanceOutstanding}
        </div>
        <div className={STYLE_TABLE_HEADER}>
          {LABELS.creditProviders.settlementAmount}
        </div>
        <div className={STYLE_TABLE_HEADER}>
          {LABELS.creditProviders.monthlyPayment}
        </div>
        <div className={STYLE_TABLE_HEADER}>
          {LABELS.creditProviders.consolidate}
        </div>
        <div className={STYLE_TABLE_HEADER}>
          {LABELS.creditProviders.isPartConsolidated}
        </div>
        <div className={STYLE_TABLE_HEADER}>&nbsp;</div>
        {fields.map((item, k) => {
          return (
            <Fragment key={item.id}>
              <div className={`${STYLE_TABLE_CELL} pl-0`}>
                <input
                  {...register(`${idKey}.${STEP_KEY}.${k}.uuid`, {
                    value: applicantsCredit?.[k]?.uuid,
                  })}
                  type="hidden"
                />
                <FormInputSelect
                  id={`${idKey}.${STEP_KEY}.${k}.providerType`}
                  register={register}
                  initSelect
                  options={ExpenditureCreditProviderTypes}
                  requiredField={false}
                  error={errorKey?.[STEP_KEY]?.[k]?.providerType?.message}
                />
              </div>
              <div className={STYLE_TABLE_CELL}>
                <FormInputText
                  id={`${idKey}.${STEP_KEY}.${k}.provider`}
                  register={register}
                  requiredField={false}
                  error={errorKey?.[STEP_KEY]?.[k]?.provider?.message}
                />
              </div>
              <div className={STYLE_TABLE_CELL}>
                <FormInputCurrency
                  id={`${idKey}.${STEP_KEY}.${k}.balanceOutstanding`}
                  register={register}
                  requiredField={false}
                  error={errorKey?.[STEP_KEY]?.[k]?.balanceOutstanding?.message}
                />
              </div>
              <div className={STYLE_TABLE_CELL}>
                <FormInputCurrency
                  id={`${idKey}.${STEP_KEY}.${k}.settlementAmount`}
                  register={register}
                  requiredField={false}
                  error={errorKey?.[STEP_KEY]?.[k]?.settlementAmount?.message}
                />
              </div>
              <div className={STYLE_TABLE_CELL}>
                <FormInputCurrency
                  id={`${idKey}.${STEP_KEY}.${k}.monthlyPayment`}
                  register={register}
                  requiredField={false}
                  error={errorKey?.[STEP_KEY]?.[k]?.monthlyPayment?.message}
                />
              </div>
              <div className={`${STYLE_TABLE_CELL} flex justify-center`}>
                <FormInputToggle
                  id={`${idKey}.${STEP_KEY}.${k}.consolidate`}
                  control={control}
                />
              </div>
              <div className={`${STYLE_TABLE_CELL} flex justify-center`}>
                <FormInputToggle
                  id={`${idKey}.${STEP_KEY}.${k}.isPartConsolidated`}
                  control={control}
                />
              </div>
              <div className={`${STYLE_TABLE_CELL} flex justify-center pr-0`}>
                {fields.length > 1 && (
                  <div className="flex items-center">
                    <CTAButton
                      secondary
                      label="X"
                      small
                      onClick={() => remove(k)}
                    />
                  </div>
                )}
              </div>
            </Fragment>
          );
        })}
      </div>

      <div className="mb-8 sm:col-span-6">
        <div className="mb-4 flex justify-start space-x-4">
          <CTAButton
            secondary
            label="Add credit"
            onClick={() => append(DEFAULT_CREDIT_PROVIDER_DATA)}
          />
        </div>
      </div>
    </div>
  );
};

export default CreditProviders;
